import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, CircularProgress, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import api from '../api';
import { conEmpresa } from '../empresa';
import { useSede } from '../contexto/Sede';
import { tablaAncha } from '../estilos/responsivo';
import Tile, { REJILLA_TILES, pesos } from './Tile';
import { CONTENEDOR_TABLA } from '../estilos/tarjeta';

const bucketConfig = {
  '0-30': { label: '0-30 dias', color: '#10b981', bg: '#d1fae5' },
  '31-60': { label: '31-60 dias', color: '#f59e0b', bg: '#fef3c7' },
  '61-90': { label: '61-90 dias', color: '#f97316', bg: '#ffedd5' },
  '90+': { label: '90+ dias', color: '#ef4444', bg: '#fee2e2' },
};

export default function Cartera() {
  const { params: paramsSede, nombre: nombreSede, sedeId } = useSede();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/cartera', { params: paramsSede }).then(res => setData(res.data)).catch(console.error).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sedeId]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  if (!data) return <Typography color="error">Error al cargar cartera</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>
            Facturas pendientes de cobro | Total: {pesos(data.total_pendiente)}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<FileDownloadIcon />}
          onClick={() => window.open(conEmpresa(`${api.defaults.baseURL}/dashboard/cartera/exportar-excel`), '_blank')}
          sx={{ borderColor: '#059669', color: '#059669', borderRadius: 1, textTransform: 'none', fontWeight: 600 }}>
          Excel
        </Button>
      </Box>

      {/* Buckets */}
      <Box sx={{ ...REJILLA_TILES, mb: 1 }}>
        {Object.entries(bucketConfig).map(([key, cfg]) => (
          <Tile key={key} label={cfg.label} v={data.resumen[key] || 0}
            color={cfg.color} bg={cfg.bg} />
        ))}
      </Box>

      <Paper elevation={0} sx={CONTENEDOR_TABLA}>
        {data.facturas.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
            <Typography sx={{ color: '#1e293b', fontSize: 16, fontWeight: 600 }}>Sin facturas pendientes</Typography>
            <Typography sx={{ color: '#64748b', fontSize: 13 }}>Todas las facturas estan al dia</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table sx={tablaAncha(780)}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>FACTURA</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>CLIENTE</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }} align="right">TOTAL</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }} align="right">PAGADO</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }} align="right">SALDO</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>DIAS</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>VENCIMIENTO</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.facturas.map((f) => {
                  const cfg = bucketConfig[f.bucket] || bucketConfig['0-30'];
                  return (
                    <TableRow key={f.id} hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
                      onClick={() => navigate(`/facturas/${f.id}`)}>
                      <TableCell sx={{ fontWeight: 600, fontSize: 13, color: '#1e3a5f' }}>{f.numero_factura}</TableCell>
                      <TableCell sx={{ fontSize: 13, color: '#1e293b' }}>{f.tercero}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 13 }}>{pesos(f.total)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, color: '#10b981' }}>{pesos(f.pagado)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: 13, color: cfg.color }}>
                        {pesos(f.saldo)}
                      </TableCell>
                      <TableCell>
                        <Chip label={`${f.dias}d`} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: cfg.bg, color: cfg.color }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: f.vencida ? '#ef4444' : '#64748b' }}>
                        {f.fecha_vencimiento ? new Date(f.fecha_vencimiento).toLocaleDateString() : '—'}
                        {f.vencida && <Chip label="VENCIDA" size="small" sx={{ ml: 1, height: 18, fontSize: 9, bgcolor: '#fee2e2', color: '#ef4444', fontWeight: 700 }} />}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
